import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
} from 'react'

import {
  Button as BaseButton,
  ButtonProps as BaseButtonProps,
  ButtonOverrides,
} from 'baseui/button'
import { useOverrides } from '../hooks/use-overrides'
import { padding } from 'polished'
import { Icon, IconType } from '../icon'
import { typography } from '@styles/typography'
import { colors } from '@styles/colors'
import { Spaces, useSpace } from '../hooks/use-space'

export interface TextButtonProps extends BaseButtonProps {
  disabled?: boolean
  leading?: IconType
  trailing?: IconType
  color?: string
  spaceTop?: Spaces
  spaceBottom?: Spaces
}

// forwardRef에 전달되는 타입 정의
type ForwardedTextButtonRef = ForwardRefExoticComponent<
  PropsWithoutRef<TextButtonProps> & RefAttributes<HTMLButtonElement>
>

export const TextButton: ForwardedTextButtonRef = forwardRef<
  HTMLButtonElement,
  TextButtonProps
>(
  (
    {
      children,
      overrides,
      leading,
      trailing,
      spaceTop,
      spaceBottom,
      color = colors.gray600,
      ...props
    },
    ref
  ) => {
    const disabledColor = colors.gray300
    const backgroundColor = 'transparent'
    const { marginTop, marginBottom } = useSpace({ spaceTop, spaceBottom })

    return (
      <BaseButton
        overrides={useOverrides<ButtonOverrides>(
          {
            BaseButton: {
              style: {
                color,
                backgroundColor,
                ...marginTop,
                ...marginBottom,
                ...typography.LabelS,
                boxSizing: 'border-box',
                fontWeight: 400,
                display: 'inline-flex',
                alignSelf: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                userSelect: 'none',
                whiteSpace: 'pre',
                ...padding('0px'),
                ':active': {
                  backgroundColor,
                },
                ':hover': {
                  color,
                  backgroundColor,
                },
                ':disabled': {
                  color: disabledColor,
                  backgroundColor,
                  ':hover': {
                    textShadow: 'none',
                    color: disabledColor,
                    backgroundColor,
                  },
                },
                ':after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-1px',
                  left: trailing && '0px',
                  right: leading && '0px',
                  width: leading || trailing ? 'calc(100% - 16px)' : '100%',
                  height: '1px',
                  backgroundColor: color,
                },
              },
            },
            StartEnhancer: {
              style: {
                marginRight: '4px',
              },
            },
            EndEnhancer: {
              style: {
                marginLeft: '4px',
              },
            },
          },
          overrides
        )}
        startEnhancer={
          leading && (
            <Icon
              type={leading}
              color={props.disabled ? disabledColor : color}
              size={12}
            />
          )
        }
        endEnhancer={
          trailing && (
            <Icon
              type={trailing}
              color={props.disabled ? disabledColor : color}
              size={12}
            />
          )
        }
        {...props}
        ref={ref}
      >
        {children}
      </BaseButton>
    )
  }
)
